import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Loader2, Zap, TrendingUp, Shield, Target, ChevronDown, ChevronUp, Crown } from 'lucide-react';
import { AntiLossStreakOptimizer, AntiLossStreakOptimizationResult, AntiLossStreakTestResult } from '@/services/anti-loss-streak-optimizer';
import { ParsedCsvRow, AnalysisParams } from './types';
import { formatCurrency } from '@/utils/formatters';

interface AutoAntiLossStreakOptimizerProps {
  csvData: ParsedCsvRow[];
  params: AnalysisParams;
  onApplySettings: (waitForLosses: number, stopAfterLosses: number) => void;
}

export const AutoAntiLossStreakOptimizer: React.FC<AutoAntiLossStreakOptimizerProps> = ({
  csvData,
  params,
  onApplySettings,
}) => {
  const [maxWaitToTest, setMaxWaitToTest] = useState(10);
  const [maxStopToTest, setMaxStopToTest] = useState(10);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [results, setResults] = useState<AntiLossStreakTestResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [optimizationMode, setOptimizationMode] = useState<'single' | 'compare'>('single');
  const [selectedMode, setSelectedMode] = useState<string>('consecutive_losses');

  const handleOptimize = async () => {
    if (csvData.length === 0) {
      return;
    }

    setIsOptimizing(true);
    try {
      // Run optimization in a setTimeout to avoid blocking UI
      setTimeout(() => {
        let optimizationResults: AntiLossStreakTestResult[];
        
        if (optimizationMode === 'compare') {
          optimizationResults = AntiLossStreakOptimizer.compareAllModes(
            csvData,
            maxWaitToTest,
            maxStopToTest,
            params
          );
        } else {
          optimizationResults = AntiLossStreakOptimizer.findTopAntiLossStreakSettings(
            csvData,
            maxWaitToTest,
            maxStopToTest,
            params,
            selectedMode
          );
        }
        
        setResults(optimizationResults);
        setIsOpen(true);
        setIsOptimizing(false);
      }, 100);
    } catch (error) {
      console.error('Anti loss streak optimization failed:', error);
      setIsOptimizing(false);
    }
  };

  const handleApplySettings = (waitForLosses: number, stopAfterLosses: number) => {
    onApplySettings(waitForLosses, stopAfterLosses);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'optimal':
        return <Badge className="bg-emerald-500/10 text-emerald-700 border-emerald-500/20">Optimal</Badge>;
      case 'good':
        return <Badge className="bg-blue-500/10 text-blue-700 border-blue-500/20">Good</Badge>;
      default:
        return <Badge className="bg-amber-500/10 text-amber-700 border-amber-500/20">Poor</Badge>;
    }
  };

  const getRatioBadge = (ratio: number) => {
    if (ratio > 2) {
      return <Badge className="bg-emerald-500/10 text-emerald-700 border-emerald-500/20">{ratio.toFixed(2)}</Badge>;
    } else if (ratio > 1) {
      return <Badge className="bg-blue-500/10 text-blue-700 border-blue-500/20">{ratio.toFixed(2)}</Badge>;
    }
    return <Badge className="bg-red-500/10 text-red-700 border-red-500/20">{ratio.toFixed(2)}</Badge>;
  };

  const getWinRateBadge = (winRate: number) => {
    if (winRate > 60) {
      return <Badge className="bg-emerald-500/10 text-emerald-700 border-emerald-500/20">{winRate.toFixed(1)}%</Badge>;
    } else if (winRate > 50) {
      return <Badge className="bg-blue-500/10 text-blue-700 border-blue-500/20">{winRate.toFixed(1)}%</Badge>;
    }
    return <Badge className="bg-amber-500/10 text-amber-700 border-amber-500/20">{winRate.toFixed(1)}%</Badge>;
  };

  const getModeBadge = (mode: string) => {
    const isLossesMode = mode === 'consecutive_losses';
    return (
      <Badge variant={isLossesMode ? "default" : "secondary"}>
        {isLossesMode ? "Losses" : "Trades"}
      </Badge>
    );
  };

  const getBestResultBadge = (index: number) => {
    if (index === 0 && optimizationMode === 'compare') {
      return <Crown className="h-4 w-4 text-yellow-500" />;
    }
    return null;
  };

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Zap className="h-5 w-5 text-primary" />
          Auto Anti Loss Streak Optimizer
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div className="space-y-3">
            <Label>Optimization Mode</Label>
            <RadioGroup 
              value={optimizationMode} 
              onValueChange={(value: 'single' | 'compare') => setOptimizationMode(value)}
              className="grid grid-cols-2 gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="single" id="single_anti" />
                <Label htmlFor="single_anti" className="text-sm">Single Mode</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="compare" id="compare_anti" />
                <Label htmlFor="compare_anti" className="text-sm">Compare Both Modes</Label>
              </div>
            </RadioGroup>
          </div>
          
          {optimizationMode === 'single' && (
            <div className="space-y-3">
              <Label>Stop Mode</Label>
              <RadioGroup 
                value={selectedMode} 
                onValueChange={setSelectedMode}
                className="grid grid-cols-1 gap-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="consecutive_losses" id="losses_mode_anti" />
                  <Label htmlFor="losses_mode_anti" className="text-sm">Stop after M consecutive losses</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="consecutive_trades" id="trades_mode_anti" />
                  <Label htmlFor="trades_mode_anti" className="text-sm">Stop after M trades (any outcome)</Label>
                </div>
              </RadioGroup>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="max-wait-to-test">Max Wait Losses to Test</Label>
              <Input
                id="max-wait-to-test"
                type="number"
                min="1"
                max="50"
                value={maxWaitToTest}
                onChange={(e) => setMaxWaitToTest(parseInt(e.target.value) || 10)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="max-stop-to-test">Max Stop Losses to Test</Label>
              <Input
                id="max-stop-to-test"
                type="number"
                min="1"
                max="50"
                value={maxStopToTest}
                onChange={(e) => setMaxStopToTest(parseInt(e.target.value) || 10)}
                className="mt-1"
              />
            </div>
          </div>
          
          <p className="text-xs text-muted-foreground">
            The optimizer will test combinations of wait losses (1-{maxWaitToTest}) and stop losses (1-{maxStopToTest})
            {optimizationMode === 'compare' && ' across both modes'}
          </p>
        </div>

        <Button 
          onClick={handleOptimize} 
          disabled={isOptimizing || csvData.length === 0}
          className="w-full"
        >
          {isOptimizing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Finding Optimal Settings...
            </>
          ) : (
            <>
              <Target className="h-4 w-4 mr-2" />
              {optimizationMode === 'compare' ? 'Compare All Modes' : 'Find Optimal Anti Loss Streak Settings'}
            </>
          )}
        </Button>

        {results.length > 0 && (
          <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="outline" className="w-full flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Optimization Results ({results.length} combinations tested)
                  {optimizationMode === 'compare' && ' across both modes'}
                </span>
                {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Wait Losses</TableHead>
                      <TableHead>Stop Losses</TableHead>
                      {optimizationMode === 'compare' && <TableHead>Mode</TableHead>}
                      <TableHead>Total Profit</TableHead>
                      <TableHead>Max DD</TableHead>
                      <TableHead>Profit/DD</TableHead>
                      <TableHead>Win Rate</TableHead>
                      <TableHead>Trades</TableHead>
                      <TableHead>Skipped</TableHead>
                      <TableHead>Effectiveness</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map((result, index) => (
                      <TableRow key={`${result.waitForLosses}-${result.stopAfterLosses}-${result.mode || 'default'}`}>
                        <TableCell className="flex items-center gap-2">
                          {getBestResultBadge(index)}
                          {getStatusBadge(result.status)}
                        </TableCell>
                        <TableCell className="font-medium">{result.waitForLosses}</TableCell>
                        <TableCell className="font-medium">{result.stopAfterLosses}</TableCell>
                        {optimizationMode === 'compare' && (
                          <TableCell>{getModeBadge(result.mode || 'consecutive_losses')}</TableCell>
                        )}
                        <TableCell className={result.totalProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                          {formatCurrency(result.totalProfit)}
                        </TableCell>
                        <TableCell className="text-red-600">
                          {formatCurrency(result.maxDrawdown)}
                        </TableCell>
                        <TableCell>{getRatioBadge(result.profitDDRatio)}</TableCell>
                        <TableCell>{getWinRateBadge(result.winRate)}</TableCell>
                        <TableCell>{result.totalTrades}</TableCell>
                        <TableCell className="text-muted-foreground">{result.skippedTrades}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {result.effectiveness.toFixed(1)}%
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            onClick={() => handleApplySettings(result.waitForLosses, result.stopAfterLosses)}
                            className="flex items-center gap-1"
                          >
                            <Shield className="h-3 w-3" />
                            Apply
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="bg-muted/30 p-4 rounded-lg">
                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                  <Shield className="h-4 w-4 text-primary" />
                  Anti Look-ahead Bias Protection
                </h4>
                <p className="text-sm text-muted-foreground">
                  This analysis maintains chronological order and tests combinations without using future data. 
                  The effectiveness metric shows what percentage of trades would be filtered out by each setting, 
                  helping you balance protection with participation rate.
                  {optimizationMode === 'compare' && (
                    <>
                      <br /><br />
                      <strong>Compare mode:</strong> Tests both "consecutive losses" and "trades" modes to find the optimal approach for your strategy.
                    </>
                  )}
                </p>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
};